import { useState, useEffect } from 'react'
import Alert from '@cloudscape-design/components/alert'
import Button from '@cloudscape-design/components/button'
import Container from '@cloudscape-design/components/container'
import FormField from '@cloudscape-design/components/form-field'
import Header from '@cloudscape-design/components/header'
import Input from '@cloudscape-design/components/input'
import ProgressBar from '@cloudscape-design/components/progress-bar'
import SegmentedControl from '@cloudscape-design/components/segmented-control'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Box from '@cloudscape-design/components/box'
import Link from '@cloudscape-design/components/link'
import Toggle from '@cloudscape-design/components/toggle'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import { useBenchmark } from '@/hooks/useBenchmark'
import { useAppStore } from '@/store/app-store'
import { filterPresetsByVendors, applyBedrockRegion, getVendorFromPreset } from '@/utils/vendor-filter'
import { isCognitoConfigured } from '@/engine/cognito-auth'
import type { PreflightResult } from '@/engine/preflight'
import { API_BASE } from '@/config/api'

interface LatencySim {
  enabled: boolean
  connectDelayMs: number
  ttftDelayMs: number
  chunkDelayMs: number
}

export function computeBenchmarkProgress(
  currentProfileIndex: number,
  totalProfiles: number,
  currentIteration: number,
  totalIterations: number,
): number {
  const totalCalls = totalProfiles * totalIterations
  if (totalCalls <= 0) return 0
  const completedCalls = (currentProfileIndex - 1) * totalIterations + currentIteration
  const pct = (completedCalls / totalCalls) * 100
  return Math.max(0, Math.min(100, pct))
}

export function BenchmarkControls() {
  const { isRunning, startBenchmark, cancelBenchmark } = useBenchmark()
  const profiles = useAppStore((s) => s.profiles)
  const presets = useAppStore((s) => s.presets)
  const executionMode = useAppStore((s) => s.executionMode)
  const hasAnthropicKey = useAppStore((s) => s.hasAnthropicKey)
  const hasXaiKey = useAppStore((s) => s.hasXaiKey)
  const selectedVendors = useAppStore((s) => s.selectedVendors)
  const progress = useAppStore((s) => s.progress)
  const isAuthenticated = useAppStore((s) => s.isAuthenticated)
  const cloudStorageEnabled = useAppStore((s) => s.cloudStorageEnabled)
  const setCloudStorageEnabled = useAppStore((s) => s.setCloudStorageEnabled)
  const credentialStatus = useAppStore((s) => s.credentialStatus)
  const [iterations, setIterations] = useState(5)
  const [preflightErrors, setPreflightErrors] = useState<PreflightResult[]>([])
  const [isPreflighting, setIsPreflighting] = useState(false)
  const [sim, setSim] = useState<LatencySim>({ enabled: false, connectDelayMs: 0, ttftDelayMs: 0, chunkDelayMs: 0 })

  useEffect(() => {
    try {
      const stored = localStorage.getItem('bedrock-diag-latency-sim')
      if (stored) setSim(JSON.parse(stored))
    } catch { /* noop */ }
  }, [])

  const updateSim = async (changes: Partial<LatencySim>) => {
    const updated = { ...sim, ...changes }
    setSim(updated)
    try { localStorage.setItem('bedrock-diag-latency-sim', JSON.stringify(updated)) } catch { /* noop */ }
  }

  const bedrockRegion = useAppStore((s) => s.bedrockRegion)

  const enabledProfiles = [
    ...filterPresetsByVendors(presets, selectedVendors)
      .filter((p) => p.enabled)
      .map((p) => getVendorFromPreset(p) === 'bedrock' ? applyBedrockRegion(p, bedrockRegion) : p),
    ...profiles.filter((p) => p.enabled)
  ]

  // Check for missing API keys on enabled profiles (local mode only - cloud mode has server-side keys)
  const missingKeys: string[] = []
  if (executionMode === 'local') {
    const hasEnabledAnthropicDirect = enabledProfiles.some((p) => {
      const profileType = 'isPreset' in p && p.isPreset ? p.profileType : (p as any).type
      return profileType === 'anthropic-direct'
    })
    const hasEnabledGrok = enabledProfiles.some((p) => {
      const profileType = 'isPreset' in p && p.isPreset ? p.profileType : (p as any).type
      return profileType === 'grok'
    })
    const hasEnabledBedrock = enabledProfiles.some((p) => {
      const profileType = 'isPreset' in p && p.isPreset ? p.profileType : (p as any).type
      return profileType === 'bedrock' || profileType === 'bedrock-cris'
    })

    if (hasEnabledAnthropicDirect && !hasAnthropicKey) {
      missingKeys.push('Anthropic API key required for enabled anthropic-direct profiles')
    }
    if (hasEnabledGrok && !hasXaiKey) {
      missingKeys.push('xAI API key required for enabled Grok profiles')
    }
    if (hasEnabledBedrock && credentialStatus !== 'verified') {
      missingKeys.push('AWS credentials required for enabled Bedrock profiles')
    }
  }
  const uniqueMissingKeys = [...new Set(missingKeys)]

  const overallPct = progress
    ? computeBenchmarkProgress(
        progress.currentProfileIndex,
        progress.totalProfiles,
        progress.currentIteration,
        progress.totalIterations,
      )
    : 0

  const latestResultSummary = progress?.latestResult
    ? `Last: ${progress.latestResult.totalTimeSec.toFixed(2)}s total, ${progress.latestResult.ttftSec.toFixed(2)}s TTFT, ${progress.latestResult.outputTokens} tokens, ${progress.latestResult.tokenThroughput} tok/s`
    : undefined

  return (
    <SpaceBetween size="m">
      <SpaceBetween direction="horizontal" size="l">
        <FormField label="Iterations per profile">
          <SpaceBetween direction="horizontal" size="xs">
            <div style={{ width: 80 }}>
              <Input
                type="number"
                value={String(iterations)}
                onChange={({ detail }) => setIterations(parseInt(detail.value, 10) || 1)}
                disabled={isRunning}
              />
            </div>
            <SegmentedControl
              selectedId={String(iterations)}
              options={[
                { id: '5', text: '5' },
                { id: '25', text: '25' },
                { id: '50', text: '50' },
                { id: '100', text: '100' },
              ]}
              onChange={({ detail }) => setIterations(parseInt(detail.selectedId, 10))}
            />
          </SpaceBetween>
        </FormField>
        <Box variant="small" color="text-body-secondary" padding={{ top: 'l' }}>
          {enabledProfiles.length} profile{enabledProfiles.length !== 1 ? 's' : ''} enabled
          {' \u2014 '}
          {enabledProfiles.length * iterations} total calls
        </Box>
        <Box padding={{ top: 'l' }}>
          <SpaceBetween direction="horizontal" size="xs">
            {isRunning ? (
              <Button variant="normal" onClick={cancelBenchmark}>Cancel</Button>
            ) : (
              <Button
                variant="primary"
                onClick={async () => {
                  setPreflightErrors([])
                  setIsPreflighting(true)
                  try {
                    const result = await startBenchmark({ iterations })
                    if (result.preflightErrors && result.preflightErrors.length > 0) {
                      setPreflightErrors(result.preflightErrors)
                    }
                  } finally {
                    setIsPreflighting(false)
                  }
                }}
                disabled={enabledProfiles.length === 0 || uniqueMissingKeys.length > 0 || isPreflighting}
                loading={isPreflighting}
              >
                {isPreflighting ? 'Checking access...' : `Run (${executionMode === 'cloud' ? 'Cloud' : 'Local'})`}
              </Button>
            )}
          </SpaceBetween>
        </Box>
      </SpaceBetween>
      {iterations >= 50 && (
        <Box variant="small" color="text-status-success">
          ✓ Good for percentile analysis (p75/p90/p95/p99)
        </Box>
      )}
      {iterations < 20 && (
        <Box variant="small" color="text-body-secondary">
          Tip: Use 50+ iterations for reliable tail latency (p95/p99) analysis
        </Box>
      )}
      {enabledProfiles.length === 0 && (
        <Alert type="warning">
          No profiles enabled.{' '}
          <span
            onClick={() => useAppStore.getState().setPage('profiles')}
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
          >
            Go to Profiles
          </span>{' '}
          and enable at least one.
        </Alert>
      )}
      {uniqueMissingKeys.length > 0 && (
        <Alert type="warning">
          {uniqueMissingKeys.map((msg, i) => (
            <div key={i}>{msg}</div>
          ))}
          <Box margin={{ top: 'xs' }}>
            <Link
              onFollow={(e) => {
                e.preventDefault()
                useAppStore.getState().setPage('settings')
              }}
              href="/settings"
            >
              Go to Settings to configure credentials
            </Link>
          </Box>
        </Alert>
      )}
      {preflightErrors.length > 0 && (
        <Alert type="error" header={`Pre-flight check failed - ${preflightErrors.length} profile${preflightErrors.length !== 1 ? 's' : ''} cannot run`}>
          <ul>
            {preflightErrors.map((e) => (
              <li key={e.profileId}>{e.profileName}: {e.error}</li>
            ))}
          </ul>
          <Box variant="small" color="text-body-secondary">
            Disable the failing profiles or fix the issues above, then try again.
          </Box>
        </Alert>
      )}
      {isRunning && progress && (
        <Container header={<Header>{progress.profileName}</Header>}>
          <SpaceBetween size="s">
            <ProgressBar
              value={overallPct}
              label={`Profile ${progress.currentProfileIndex}/${progress.totalProfiles}`}
              description={`${progress.currentIteration}/${progress.totalIterations} iterations`}
              additionalInfo={latestResultSummary}
            />
          </SpaceBetween>
        </Container>
      )}
      <Box variant="small" color="text-body-secondary" padding={{ top: 's' }}>
        Tips: Run 3+ iterations for variance. First call may be slower (SDK init, DNS). Use same prompt/tokens across profiles for fair comparison.
      </Box>

      {/* ── Cloud Storage Toggle / Browser-Only Banner (local mode) ── */}
      {executionMode === 'local' && isCognitoConfigured() && (
        isAuthenticated ? (
          <Alert type="info" statusIconAriaLabel="Info">
            <SpaceBetween direction="horizontal" size="xs">
              <Toggle
                checked={cloudStorageEnabled}
                onChange={({ detail }) => setCloudStorageEnabled(detail.checked)}
              >
                Save results to cloud
              </Toggle>
            </SpaceBetween>
          </Alert>
        ) : (
          <Alert type="info" statusIconAriaLabel="Info">
            Results are stored in your browser only. Sign in to enable cloud storage.
          </Alert>
        )
      )}

      {/* ── Latency Simulation ───────────────────────────── */}
      <Container header={<Header description="Inject artificial delays to simulate bad network conditions (slow proxy, VPN, high-latency routing).">🔧 Network Latency Simulation</Header>}>
        <SpaceBetween size="m">
          <Toggle
            checked={sim.enabled}
            onChange={({ detail }) => updateSim({ enabled: detail.checked })}
          >
            Enable latency simulation
          </Toggle>
          {sim.enabled && (
            <>
              <StatusIndicator type="warning">Active - benchmarks will include simulated delays</StatusIndicator>
              <ColumnLayout columns={3}>
                <FormField label="Connect delay (ms)" description="Simulates slow TCP/TLS handshake. Added before the API call.">
                  <Input
                    type="number"
                    value={String(sim.connectDelayMs)}
                    onChange={({ detail }) => updateSim({ connectDelayMs: parseInt(detail.value) || 0 })}
                  />
                </FormField>
                <FormField label="TTFT delay (ms)" description="Simulates slow first-byte routing. Added after first chunk arrives.">
                  <Input
                    type="number"
                    value={String(sim.ttftDelayMs)}
                    onChange={({ detail }) => updateSim({ ttftDelayMs: parseInt(detail.value) || 0 })}
                  />
                </FormField>
                <FormField label="Per-chunk delay (ms)" description="Simulates bandwidth throttling. Added per streaming chunk.">
                  <Input
                    type="number"
                    value={String(sim.chunkDelayMs)}
                    onChange={({ detail }) => updateSim({ chunkDelayMs: parseInt(detail.value) || 0 })}
                  />
                </FormField>
              </ColumnLayout>
            </>
          )}
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  )
}
